import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getBookApi, createOrderApi, verifyPaymentApi, mockPayApi } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const S = process.env.REACT_APP_API_URL?.replace('/api','') || '';

export default function BookDetail() {
  const { id } = useParams();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [book, setBook]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying]   = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    getBookApi(id)
      .then(({data}) => setBook(data))
      .catch(() => navigate('/books'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const owned = book?.hasPurchased || (user?.purchasedBooks||[]).some(b=>(b._id||b)===id);

  const handleBuy = async () => {
    if (!user) return navigate('/login');
    setPaying(true); setMsg('');
    try {
      const { data } = await createOrderApi(id, 'book');
      if (data.mock) {
        await mockPayApi(id, 'book');
        await refreshUser();
        setBook(p => ({...p, hasPurchased:true}));
        setMsg('✅ Payment successful! Full access granted.');
      } else {
        const options = {
          key: data.keyId, amount: data.amount, currency: data.currency,
          name: 'BookPub', description: book.title, order_id: data.orderId,
          handler: async (resp) => {
            try {
              await verifyPaymentApi({...resp, purchaseId: data.purchaseId});
              await refreshUser();
              setBook(p=>({...p,hasPurchased:true}));
              setMsg('✅ Payment verified! Full access granted.');
            } catch { setMsg('❌ Verification failed. Contact support.'); }
          },
          prefill: { name: user.name, email: user.email },
          theme: { color: '#e8c547' },
        };
        new window.Razorpay(options).open();
      }
    } catch(e) { setMsg(e.response?.data?.message || '❌ Payment failed.'); }
    finally { setPaying(false); }
  };

  if (loading) return <div className="spinner" style={{marginTop:120}} />;
  if (!book) return null;

  const cover = book.coverImage
    ? (book.coverImage.startsWith('http') ? book.coverImage : `${S}${book.coverImage}`)
    : null;
  const sorted = [...(book.chapters||[])].sort((a,b)=>a.chapterNumber-b.chapterNumber);

  return (
    <div style={{ minHeight:'80vh' }}>
      {/* Hero */}
      <div style={{ background:'linear-gradient(160deg,var(--bg2) 0%,var(--bg) 100%)', borderBottom:'1px solid var(--border)', padding:'60px 0 48px' }}>
        <div className="container">
          <div style={{ display:'flex', gap:48, alignItems:'flex-start', flexWrap:'wrap' }}>
            {/* Cover */}
            <div style={{ flexShrink:0, width:200, borderRadius:12, overflow:'hidden', boxShadow:'0 32px 64px rgba(0,0,0,0.6)' }}>
              {cover
                ? <img src={cover} alt={book.title} style={{ width:'100%', display:'block' }} />
                : <div style={{ background:'linear-gradient(135deg,var(--bg3),#3d2060)', aspectRatio:'2/3', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'4rem' }}>📚</div>
              }
            </div>

            {/* Info */}
            <div style={{ flex:1, minWidth:260, display:'flex', flexDirection:'column', gap:14 }}>
              <span className="badge badge-amber">{book.genre}</span>
              <h1 style={{ fontFamily:'var(--font-d)', fontSize:'clamp(1.8rem,4vw,3rem)', fontWeight:600, color:'var(--text)', lineHeight:1.15 }}>{book.title}</h1>
              <p style={{ color:'var(--text3)', fontSize:'0.95rem' }}>by <strong style={{color:'var(--text2)'}}>{book.author||'Admin'}</strong></p>

              {book.rating?.count>0 && (
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  {[...Array(5)].map((_,i)=><span key={i} style={{color:i<Math.round(book.rating.average)?'var(--gold)':'var(--bg3)',fontSize:'1.1rem'}}>★</span>)}
                  <span style={{color:'var(--text3)',fontSize:'0.85rem'}}>{book.rating.average?.toFixed(1)} ({book.rating.count})</span>
                </div>
              )}

              <p 
                style={{ 
                  color: 'var(--text2)', 
                  lineHeight: 1.9,     // smoother reading
                  fontSize: '1.05rem', // slightly bigger
                  maxWidth: 650,       
                  whiteSpace: "pre-line",
                  margin: "20px auto"
                }}
              >
                {book.description}
              </p>

              {book.tags?.length>0 && (
                <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                  {book.tags.map(t=><span key={t} style={{padding:'3px 12px',border:'1px solid var(--border2)',borderRadius:20,color:'var(--text3)',fontSize:'0.78rem'}}>{t}</span>)}
                </div>
              )}

              {/* Purchase / stats */}
              <div style={{ marginTop:8, display:'flex', flexDirection:'column', gap:14 }}>
                <div style={{ display:'flex', gap:24, color:'var(--text3)', fontSize:'0.85rem' }}>
                  <span>📄 {sorted.length} chapters</span>
                  <span>🆓 {sorted.filter(c=>c.isFree).length} free</span>
                  {book.totalReads>0 && <span>👁 {book.totalReads.toLocaleString()} reads</span>}
                </div>

                {owned ? (
                  <div style={{ display:'inline-flex', alignItems:'center', gap:8, background:'rgba(44,182,125,0.12)', color:'var(--success)', padding:'12px 20px', borderRadius:10, fontWeight:700, width:'fit-content' }}>
                    ✅ You own this book — all chapters unlocked
                  </div>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:10, alignItems:'flex-start' }}>
                    <span style={{ fontFamily:'var(--font-d)', fontSize:'2.2rem', fontWeight:600, color: book.price===0?'var(--success)':'var(--gold)' }}>
                      {book.price===0 ? 'Free' : `₹${book.price}`}
                    </span>
                    {book.price>0 && (
                      <button className="btn btn-gold btn-lg" onClick={handleBuy} disabled={paying}>
                        {paying ? 'Processing…' : `Buy Full Book — ₹${book.price}`}
                      </button>
                    )}
                    {msg && <p style={{color:msg.startsWith('✅')?'var(--success)':'var(--accent)',fontSize:'0.9rem',marginTop:4}}>{msg}</p>}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Chapter list */}
      <div className="container" style={{ padding:'48px 24px 80px' }}>
        <h2 style={{ fontFamily:'var(--font-d)', fontSize:'1.8rem', color:'var(--text)', marginBottom:24 }}>Chapters</h2>
        <div style={{ border:'1px solid var(--border)', borderRadius:var_radius_lg, overflow:'hidden' }}>
          {sorted.map((ch, idx) => {
            const chOwned = owned || ch.isFree
              || (user?.purchasedChapters||[]).some(c=>(c._id||c)===ch._id);
            return (
              <div key={ch._id} style={{
                display:'flex', justifyContent:'space-between', alignItems:'center',
                padding:'18px 24px', background: idx%2===0 ? 'var(--surface)' : 'var(--bg2)',
                borderBottom:'1px solid var(--border)', transition:'background 0.15s',
              }}>
                <div style={{ display:'flex', gap:16, alignItems:'center' }}>
                  <span style={{ fontFamily:'var(--font-d)', fontWeight:700, color:'var(--gold)', fontSize:'0.85rem', minWidth:48 }}>Ch.{ch.chapterNumber}</span>
                  <div>
                    <p style={{ fontWeight:700, color:'var(--text)', fontSize:'0.93rem' }}>{ch.title}</p>
                    <p style={{ color:'var(--text3)', fontSize:'0.78rem', marginTop:3, display:'flex', gap:12 }}>
                      <span>{ch.wordCount?.toLocaleString()||0} words</span>
                      {ch.readCount>0 && <span>👁 {ch.readCount.toLocaleString()}</span>}
                      {ch.likeCount>0 && <span>♥ {ch.likeCount}</span>}
                    </p>
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  {ch.isFree ? <span className="badge badge-free">Free</span> : <span className="badge badge-locked">🔒 Paid</span>}
                  {chOwned
                    ? <Link to={`/books/${id}/chapters/${ch._id}`} className="btn btn-gold btn-sm">Read</Link>
                    : <span style={{color:'var(--text3)',fontSize:'0.82rem'}}>Unlock book</span>
                  }
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// small helper so JSX doesn't inline the literal string
const var_radius_lg = 'var(--radius-lg)';
